import { __assign, __read } from "tslib";
import { UploadSource, UploadStatus, } from '@office-start/upload-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
var uploadStateSubject = new BehaviorSubject({
    v1FileUploadState: {
        sequenceNumber: 0,
        file: null,
        uploadStatus: UploadStatus.Started,
        telemetry: {
            source: UploadSource.Unknown,
            area: 'Other',
        },
    },
    fileUploadStates: {},
    nextSequenceNumber: 1,
});
export function useUploadState() {
    var _a = __read(useState(uploadStateSubject.getValue()), 2), uploadState = _a[0], setUploadState = _a[1];
    useEffect(function () {
        var sub = uploadStateSubject.subscribe(function (state) {
            return setUploadState(state);
        });
        return function () { return sub.unsubscribe(); };
    }, []);
    var setV1FileUploadState = useCallback(function (fileUploadState, uploadStatus, file) {
        var prev = uploadStateSubject.getValue();
        var newV1FileUploadState = __assign(__assign({}, prev.v1FileUploadState), { uploadStatus: uploadStatus !== undefined
                ? uploadStatus
                : prev.v1FileUploadState.uploadStatus, file: file !== undefined ? file : prev.v1FileUploadState.file });
        uploadStateSubject.next(__assign(__assign({}, prev), { v1FileUploadState: fileUploadState !== undefined
                ? fileUploadState
                : newV1FileUploadState }));
    }, []);
    // callback for adding a new file upload to overall state
    var addFileUploadState = useCallback(function (newFileUploadState) {
        var _a;
        var prev = uploadStateSubject.getValue();
        var nextSequenceNumber = newFileUploadState.sequenceNumber + 1;
        var newState = __assign(__assign({}, prev), { nextSequenceNumber: nextSequenceNumber, fileUploadStates: __assign(__assign({}, prev.fileUploadStates), (_a = {}, _a[newFileUploadState.sequenceNumber] = newFileUploadState, _a)) });
        uploadStateSubject.next(newState);
    }, []);
    // callback for updating any data related to upload state for given file
    var updateFileUploadState = useCallback(function (data) {
        var _a;
        var prev = uploadStateSubject.getValue();
        var prevFileState = prev.fileUploadStates[data.sequenceNumber];
        if (!prevFileState) {
            return;
        }
        var shouldUpdateState = false;
        var isUploadAlreadyInEndState = prevFileState.uploadStatus === UploadStatus.Cancelled ||
            prevFileState.uploadStatus === UploadStatus.Completed ||
            prevFileState.uploadStatus === UploadStatus.Failed ||
            prevFileState.uploadStatus === UploadStatus.Unsupported;
        var newFileUploadState = __assign({}, prevFileState);
        if (!isUploadAlreadyInEndState ||
            (prevFileState.uploadStatus === UploadStatus.Failed &&
                data.uploadStatus === UploadStatus.Queued)) {
            // to avoid race condition where upload progress is reported after it has been marked canceled, failed, etc
            // update the progress and status only if upload is not already in end state.
            // exception is retry where a failed upload status can be changed to queued.
            if (data.progress !== undefined) {
                newFileUploadState.progress = data.progress;
                shouldUpdateState = true;
            }
            if (data.uploadStatus !== undefined) {
                newFileUploadState.uploadStatus = data.uploadStatus;
                shouldUpdateState = true;
            }
        }
        if (data.fileUrlModel !== undefined) {
            newFileUploadState.fileUrlModel = data.fileUrlModel;
            shouldUpdateState = true;
        }
        if (data.error !== undefined) {
            newFileUploadState.error = data.error;
            shouldUpdateState = true;
        }
        if (data.parent !== undefined) {
            newFileUploadState.parent = data.parent;
            shouldUpdateState = true;
        }
        if (shouldUpdateState) {
            var newState = __assign(__assign({}, prev), { fileUploadStates: __assign(__assign({}, prev.fileUploadStates), (_a = {}, _a[newFileUploadState.sequenceNumber] = newFileUploadState, _a)) });
            uploadStateSubject.next(newState);
        }
    }, []);
    // callback for dismissing upload status for given file (this will remove any data corresponding to this file from state)
    // only happens if file upload is not in progress
    var removeFileUploadState = useCallback(function (sequenceNumber) {
        var prev = uploadStateSubject.getValue();
        if (prev.fileUploadStates[sequenceNumber]) {
            var fileUploadStatus = prev.fileUploadStates[sequenceNumber].uploadStatus;
            if (fileUploadStatus !== UploadStatus.Queued &&
                fileUploadStatus !== UploadStatus.Uploading) {
                var fileUploadStates = __assign({}, prev.fileUploadStates);
                delete fileUploadStates[sequenceNumber];
                var newState = __assign(__assign({}, prev), { fileUploadStates: fileUploadStates });
                uploadStateSubject.next(newState);
            }
        }
    }, []);
    var setShowUploadFileUnsupportedDialog = useCallback(function (showUploadFileUnsupportedDialog) {
        var prev = uploadStateSubject.getValue();
        var newState = __assign(__assign({}, prev), { showUploadFileUnsupportedDialog: showUploadFileUnsupportedDialog });
        uploadStateSubject.next(newState);
    }, []);
    return useMemo(function () { return ({
        uploadState: uploadState,
        addFileUploadState: addFileUploadState,
        updateFileUploadState: updateFileUploadState,
        removeFileUploadState: removeFileUploadState,
        setShowUploadFileUnsupportedDialog: setShowUploadFileUnsupportedDialog,
        setV1FileUploadState: setV1FileUploadState,
    }); }, [
        addFileUploadState,
        removeFileUploadState,
        setShowUploadFileUnsupportedDialog,
        setV1FileUploadState,
        updateFileUploadState,
        uploadState,
    ]);
}
//# sourceMappingURL=useUploadState.js.map